import { useCallback, useEffect, useRef, useState } from 'react';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  Box,
  Button,
  Center,
  HStack,
  Heading,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Hourglass, Trash2, Users } from 'lucide-react';
import { getDefaultStore, useAtomValue } from 'jotai';
import { WS_EVENTS } from '@twinden/shared';
import { api, ApiError, type MyJoinRequest } from '../../lib/api';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useFamily } from '../../hooks/useFamily';
import { useT } from '../../i18n';
import { currentUserAtom } from '../../store';
import { familyGoneText } from '../../hooks/useFamilyDeleted';
import {
  dismissDeletedNote,
  familyDeletedNotesAtom,
} from '../../lib/familyDeletedNote';

// Signed in with no family (D153, D156): the requests waiting for an answer,
// or how to get a link. The shell shows only this, Settings and sign out.
export function WaitingForFamily() {
  const t = useT();
  const toast = useToast();
  const { onEvent, connectionEpoch } = useWebSocket();
  const { loadFamilies } = useFamily();
  const [requests, setRequests] = useState<MyJoinRequest[] | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  // "The Sharma Family was deleted by Asha." until dismissed (M54, D189).
  const deletedNotes = useAtomValue(familyDeletedNotesAtom);
  const userId = useAtomValue(currentUserAtom)?.id;
  // useT/useToast return new functions each render; keep `load` stable.
  const say = useRef({ t, toast });
  say.current = { t, toast };

  const load = useCallback(async () => {
    try {
      setRequests((await api.getMyJoinRequests()).data.requests);
    } catch (err) {
      setRequests((current) => current ?? []);
      say.current.toast({
        status: 'error',
        title:
          err instanceof ApiError
            ? err.message
            : say.current.t('account.waiting.loadFailed'),
      });
    }
  }, []);

  // Again after a reconnect: an answer may have come while offline.
  useEffect(() => {
    void load();
    if (connectionEpoch > 0) void loadFamilies();
  }, [load, loadFamilies, connectionEpoch]);
  // The mark while the requests load (M66, D248).
  const waiting = useLoadingState({ loading: requests === null });

  useEffect(
    () =>
      onEvent(({ event }) => {
        if (event === WS_EVENTS.JOIN_REQUEST_DECIDED) void load();
      }),
    [onEvent, load]
  );

  const cancel = async (request: MyJoinRequest) => {
    setCancelling(request.id);
    try {
      await api.cancelJoinRequest(request.id);
      setRequests(
        (current) => current?.filter((r) => r.id !== request.id) ?? null
      );
      toast({ status: 'success', title: t('account.waiting.cancelled') });
    } catch (err) {
      toast({
        status: 'error',
        title:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
      });
      void load();
    } finally {
      setCancelling(null);
    }
  };

  if (requests === null || waiting.show) {
    return (
      <Center h="full">
        <LoadingPanel state={waiting} />
      </Center>
    );
  }

  const pending = requests.filter((r) => r.status === 'pending');
  // Newest first: the last answer, if it was a no (D155).
  const lastDeclined =
    pending.length === 0 && requests[0]?.status === 'declined'
      ? requests[0]
      : null;

  return (
    <Box h="full" overflowY="auto" px={4} py={8}>
      <VStack spacing={5} maxW="420px" mx="auto" align="stretch">
        {deletedNotes.map((note) => (
          <HStack
            key={note.familyId}
            role="status"
            bg="danger.tint"
            borderRadius="card"
            p={4}
            spacing={3}
            align="center"
          >
            <Box color="danger" flexShrink={0} aria-hidden="true">
              <Trash2 size={22} />
            </Box>
            <Text flex={1} fontWeight={600} color="ink">
              {familyGoneText(note.familyName, note.deletedByName)}
            </Text>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              bg="surface"
              borderColor="line"
              onClick={() =>
                userId &&
                dismissDeletedNote(userId, note.familyId, getDefaultStore())
              }
            >
              {t('account.deleteFamily.dismiss')}
            </Button>
          </HStack>
        ))}
        {pending.length > 0 ? (
          pending.map((request) => (
            <VStack
              key={request.id}
              bg="surface"
              borderRadius="card"
              borderWidth="1px"
              borderColor="line"
              p={6}
              spacing={4}
              textAlign="center"
            >
              <Hourglass size={32} aria-hidden="true" />
              <Heading as="h2" fontSize="24px" lineHeight="30px">
                {t('account.join.waitingTitle', {
                  family: request.familyName,
                })}
              </Heading>
              {request.familyHandle && (
                <Text color="muted" fontWeight={600} mt={-2}>
                  @{request.familyHandle}
                </Text>
              )}
              <Text color="ink.body">{t('account.join.waitingBody')}</Text>
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={() => void cancel(request)}
                isLoading={cancelling === request.id}
              >
                {t('account.waiting.cancel')}
              </Button>
            </VStack>
          ))
        ) : (
          <VStack spacing={4} textAlign="center" pt={8}>
            <Users size={40} aria-hidden="true" />
            <Heading as="h2" fontSize="28px" lineHeight="34px">
              {t('account.waiting.noneTitle')}
            </Heading>
            {lastDeclined && (
              <Text fontWeight={600}>
                {t('account.join.notAddedThisTime', {
                  family: lastDeclined.familyName,
                })}
              </Text>
            )}
            <Text color="ink.body" fontSize="17px" lineHeight="25px">
              {t('account.waiting.noneBody')}
            </Text>
          </VStack>
        )}
      </VStack>
    </Box>
  );
}
